import React, { useMemo, useState } from "react";
import { useFetchList } from "../../hooks/useFetchList";
import { inferFirstKey } from "../../api";

const colors = [
  "#4CAF50", // Green
  "#9C27B0", // Purple
  "#FF9800", // Orange
  "#00BCD4", // Cyan
  "#F44336", // Red
  "#2196F3", // Blue
  "#FF5722", // Deep Orange
  "#607D8B", // Blue Grey
  "#795548", // Brown
  "#E91E63", // Pink
  "#673AB7", // Deep Purple
  "#3F51B5", // Indigo
  "#009688", // Teal
  "#8BC34A", // Light Green
  "#CDDC39", // Lime
  "#FFEB3B", // Yellow
  "#FFC107", // Amber
  "#FF6F00", // Orange Accent
  "#E65100", // Deep Orange Accent
  "#BF360C", // Red Accent
  "#D32F2F", // Red Darken
  "#C2185B", // Pink Darken
  "#7B1FA2", // Purple Darken
  "#512DA8", // Deep Purple Darken
  "#303F9F", // Indigo Darken
  "#1976D2", // Blue Darken
  "#0288D1", // Light Blue Darken
  "#0097A7", // Cyan Darken
  "#00796B", // Teal Darken
  "#388E3C", // Green Darken
];

export default function TopProductsChart({ headers = {} }) {
  const { data, loading, error } = useFetchList("/v1/frontend/topProducts", {
    headers,
  });
  const [hoveredItem, setHoveredItem] = useState(null);

  const chartData = useMemo(() => {
    if (!data || data.length === 0) return null;
    const keys = Object.keys(data[0] || {});
    const nameKey = inferFirstKey(keys, [/(product|name|item)$/i]);
    const valKey = inferFirstKey(
      keys.filter((k) => k !== nameKey),
      [/(sales_amount)/i]
    );

    const items = data
      .map((r) => ({
        name: r[nameKey],
        value: Number(r[valKey]) || 0,
      }))
      .sort((a, b) => b.value - a.value);

    const totalValue = items.reduce((sum, item) => sum + item.value, 0);
    const maxValue = Math.max(...items.map((item) => item.value));

    return items.map((item, index) => ({
      ...item,
      barPercentage: maxValue > 0 ? (item.value / maxValue) * 100 : 0,
      actualPercentage:
        totalValue > 0 ? ((item.value / totalValue) * 100).toFixed(1) : 0,
      color: colors[index % colors.length],
    }));
  }, [data]);

  if (loading) return <div style={{ textAlign: "center" }}>Loading...</div>;
  if (error) return <div style={{ color: "#d33" }}>Failed to load.</div>;
  if (!chartData) return <div>No data.</div>;
  return (
    <div
      style={{
        width: "100%",
        minHeight: 220,
        maxHeight: 340,
        padding: "10px",
        boxSizing: "border-box",
      }}
    >
      {chartData.map((item, index) => (
        <div
          key={index}
          style={{
            marginBottom: "20px",
            fontSize: "14px",
          }}
        >
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: "6px",
            }}
          >
            <div
              style={{
                fontWeight: "500",
                color: "#666",
                fontSize: "13px",
              }}
            >
              {item.name}
            </div>
            <div
              style={{
                fontSize: "12px",
                fontWeight: "600",
                color: "#333",
              }}
            >
              ${item.value}
            </div>
          </div>
          <div
            style={{
              width: "100%",
              height: "6px",
              backgroundColor: "#f0f0f0",
              borderRadius: "3px",
              position: "relative",
            }}
            onMouseEnter={() => setHoveredItem(index)}
            onMouseLeave={() => setHoveredItem(null)}
          >
            <div
              style={{
                height: "100%",
                width: `${item.barPercentage}%`,
                backgroundColor: item.color,
                borderRadius: "3px",
                transition: "width 0.3s ease, opacity 0.2s ease",
                opacity: hoveredItem === index ? "0.8" : "1",
              }}
            />
            {hoveredItem === index && (
              <div
                style={{
                  position: "absolute",
                  bottom: "12px",
                  left: "50%",
                  transform: "translateX(-50%)",
                  backgroundColor: "rgba(0, 0, 0, 0.8)",
                  color: "white",
                  padding: "4px 8px",
                  borderRadius: "4px",
                  fontSize: "12px",
                  whiteSpace: "nowrap",
                  zIndex: 1000,
                  pointerEvents: "none",
                }}
              >
                {item.actualPercentage}%
              </div>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}
